import { getPaymentMethodIcon } from "@/constants";
import { ConstantResource, PaymentPlanResource, PromotionResource, SubscriptionActiveResource } from "@/interfaces";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { cn } from "@/lib/utils";
import { useOnlinePaymentOptions } from "@/hooks/payments/useOnlinePaymentOptions";
import { formatCurrency, formatDate } from "@/utils/currency";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { StatusChip } from "./PaymentStatusChip";
import { TableSkeleton } from "./TableSkeleton";
import { TicketPercent } from "lucide-react";
import RadioDot from "./payment/RadioDot";
import PaymentTimingChoice from "./payment-timing-choice";
import PayInFullChoice from "./pay-in-full-choice";
import { PaymentTiming } from "@/interfaces";
import { planMissesPromotionDeadline, planFirstPaymentDate } from "@/utils/promotion-deadline";
import { planHasEligiblePromotion } from "@/utils/promotions";
import { planAllowsPayInFull } from "@/utils/pay-in-full";
import { emptyScheduleReason } from "@/utils/empty-schedule";

type Props = {
  contractId: string;
  paymentPlans?: PaymentPlanResource[];

  selectedPaymentMethodType?: ConstantResource | null;
  setSelectedPaymentMethodType: (type: ConstantResource | null) => void;

  selectedPaymentPlan?: PaymentPlanResource | null;
  setSelectedPaymentPlan: (plan: PaymentPlanResource | null) => void;

  hasActiveSubscription: boolean;
  subscriptionActive?: SubscriptionActiveResource;

  appliedPromo?: any;

  paymentTiming: PaymentTiming;
  setPaymentTiming: (timing: PaymentTiming) => void;

  payInFull: boolean;
  setPayInFull: (payInFull: boolean) => void;
};

type TransactionType = { date: string; amount: number };

export function FlowPagePaymentMethodAccordion({
  contractId,
  paymentPlans,
  selectedPaymentMethodType,
  setSelectedPaymentMethodType,
  selectedPaymentPlan,
  setSelectedPaymentPlan,
  hasActiveSubscription,
  subscriptionActive,
  appliedPromo,
  paymentTiming,
  setPaymentTiming,
  payInFull,
  setPayInFull,
}: Props) {
  const { t } = useTranslation();

  const dedupePlansByInstallments = (plans: PaymentPlanResource[]) => {
    const picked = new Map<number, PaymentPlanResource>();

    for (const plan of plans) {
      const inst = plan.attributes.installments;
      const existing = picked.get(inst);

      // first one wins by default
      if (!existing) {
        picked.set(inst, plan);
        continue;
      }

      // if existing has no promo and new has promo, replace
      if (!planHasEligiblePromotion(existing) && planHasEligiblePromotion(plan)) {
        picked.set(inst, plan);
      }
    }

    // return sorted by installments
    return Array.from(picked.values()).sort(
      (a, b) => a.attributes.installments - b.attributes.installments
    );
  };

  const countDiscountedInstallments = (plans: PaymentPlanResource[]) => {
    const deduped = dedupePlansByInstallments(plans);
    return deduped.filter(planHasEligiblePromotion).length;
  };

  const { methods, plansByMethodId, installmentsRangeByMethodId, discountCountByMethodId } =
    useMemo(() => {
      const methodsMap = new Map<string, ConstantResource>();
      const plansByMethodId = new Map<string, PaymentPlanResource[]>();
      const rangeByMethodId = new Map<string, { min: number; max: number }>();

      for (const plan of paymentPlans || []) {
        const installments = plan.attributes.installments;

        for (const method of plan.relationships?.paymentMethodTypes?.data ?? []) {
          methodsMap.set(method.id, method as any);

          const arr = plansByMethodId.get(method.id) ?? [];
          arr.push(plan);
          plansByMethodId.set(method.id, arr);

          const range = rangeByMethodId.get(method.id);
          rangeByMethodId.set(method.id, {
            min: range ? Math.min(range.min, installments) : installments,
            max: range ? Math.max(range.max, installments) : installments,
          });
        }
      }

      const discountCountByMethodId = new Map<string, number>();
      for (const [methodId, plans] of plansByMethodId.entries()) {
        discountCountByMethodId.set(methodId, countDiscountedInstallments(plans));
      }

      // stable ordering: online first (500), then others
      const methods = Array.from(methodsMap.values()).sort((a, b) => {
        if (a.id === "500") return -1;
        if (b.id === "500") return 1;
        return (a.attributes?.value ?? "").localeCompare(b.attributes?.value ?? "");
      });

      return {
        methods,
        plansByMethodId,
        installmentsRangeByMethodId: rangeByMethodId,
        discountCountByMethodId,
      };
    }, [paymentPlans, appliedPromo]);

  const integratedMethods = methods.filter((m) => m.id === "500");
  const offlineMethods = methods.filter((m) => m.id !== "500");

  const value = selectedPaymentMethodType?.id ?? "";

  const onSelectMethod = (m: ConstantResource) => {
    if (hasActiveSubscription) return;
    if (selectedPaymentMethodType?.id === m.id) {
      setSelectedPaymentMethodType(null);
      setSelectedPaymentPlan(null);
    } else {
      setSelectedPaymentMethodType(m);
      setSelectedPaymentPlan(null);
    }
  };

  const renderMethodItem = (m: ConstantResource) => {
    const Icon = getPaymentMethodIcon(m.id);
    const plans = dedupePlansByInstallments(plansByMethodId.get(m.id) ?? []);

    const range = installmentsRangeByMethodId.get(m.id);
    const rangeLabel =
      range && range.min !== range.max
        ? `${range.min} ${t("fe.pages.flow.steps.paymentMethod.to")} ${range.max} ${t("fe.pages.flow.steps.paymentMethod.installments", {
            defaultValue: "installments",
          })}`
        : range
          ? `${range.min} ${t("fe.pages.flow.steps.paymentMethod.installment", {
              defaultValue: "installment",
            })}`
          : undefined;

    const discountCount = discountCountByMethodId.get(m.id) ?? 0;

    const isOpen = value === m.id;

    return (
      <AccordionItem
        key={m.id}
        value={m.id}
        className={cn(
          "rounded-2xl",
          isOpen ? "border-primary border-2" : "border-1 border-muted-foreground/20"
        )}
      >
        <AccordionTrigger
          className={cn(
            "px-5 py-4 no-underline hover:no-underline",
            isOpen ? "bg-muted/30" : "bg-muted/10"
          )}
          onClick={(e) => {
            e.preventDefault();
            onSelectMethod(m);
          }}
        >
          <MethodHeader
            checked={isOpen}
            icon={Icon}
            label={t("fe.pages.flow.steps.paymentMethod.paymentMethods." + m.attributes.key)}
            rangeLabel={rangeLabel}
            discountCount={discountCount}
          />
        </AccordionTrigger>

        <AccordionContent className="px-5 pb-6 pt-2 h-full rounded-2xl">
          {/* Installments selector */}
          <div className="mt-4">
            <div className="text-lg font-semibold text-foreground">
              {t("fe.pages.flow.steps.paymentMethod.paymentPlansTitle", {
                defaultValue: "Nombre de versements",
              })}
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {plans.map((plan) => {
                const checked = Number(selectedPaymentPlan?.id) === Number(plan.id);
                const promo = plan.relationships?.eligiblePromotions?.data?.[0]?.attributes;

                const promoValue = promo?.amount
                  ? `${Math.abs(promo.amount)}$`
                  : promo?.percent
                    ? `${promo.percent}%`
                    : null;
                const subtitle =
                  !appliedPromo && promoValue ? (
                    <span className="flex flex-col items-start leading-tight">
                      <span>
                        {t("fe.pages.flow.steps.paymentMethod.discount", {
                          defaultValue: "Rabais de",
                        })}{" "}
                        {promoValue}
                      </span>
                      {promo?.name && (
                        <span className="font-medium">{promo.name}</span>
                      )}
                    </span>
                  ) : undefined;

                return (
                  <InstallmentCard
                    key={plan.id}
                    checked={checked}
                    disabled={hasActiveSubscription}
                    title={<span className="text-base">{plan.attributes.installments}</span>}
                    subtitle={subtitle}
                    onClick={() => {
                      if (hasActiveSubscription) return;
                      setSelectedPaymentPlan(checked ? null : plan);
                    }}
                  />
                );
              })}
            </div>
          </div>
          {selectedPaymentPlan?.id && !hasActiveSubscription ? (
            <PaymentSchedule
              contractId={contractId}
              paymentPlanId={selectedPaymentPlan.id}
              promotion={selectedPaymentPlan.relationships?.eligiblePromotions?.data?.[0]}
              promotionId={selectedPaymentPlan.relationships?.eligiblePromotions?.data?.[0]?.id}
              promotionStopAt={selectedPaymentPlan.relationships?.eligiblePromotions?.data?.[0]?.attributes?.stop_at}
              planStartDate={planFirstPaymentDate(selectedPaymentPlan)}
              installments={selectedPaymentPlan.attributes?.installments}
              paymentTiming={paymentTiming}
              setPaymentTiming={setPaymentTiming}
              payInFullAllowed={planAllowsPayInFull(selectedPaymentPlan)}
              payInFull={payInFull}
              setPayInFull={setPayInFull}
              hasActiveSubscription={hasActiveSubscription}
              subscriptionActive={subscriptionActive}
            />
          ) : null}
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <div className="space-y-8">
      {/* Integrated */}
      {integratedMethods.length > 0 && (
        <div>
          <div className="follo-label mb-4">
            {t("fe.pages.flow.steps.paymentMethod.title", {
              defaultValue: "Méthode de paiement intégré",
            })}
          </div>

          <Accordion
            type="single"
            collapsible
            value={value || undefined}
            className="space-y-4"
          >
            {integratedMethods.map(renderMethodItem)}
          </Accordion>
        </div>
      )}

      {/* Offline */}
      <div>
        <div className="follo-label mb-4">
          {t("fe.pages.flow.steps.paymentMethod.titleManual", {
            defaultValue: "Méthode de paiement manuel hors plateforme",
          })}
        </div>

        <Accordion
          type="single"
          collapsible
          value={value || undefined}
          className="space-y-4"
        >
          {offlineMethods.map(renderMethodItem)}
        </Accordion>
      </div>
    </div>
  );
}

function MethodHeader({
  checked,
  icon: Icon,
  label,
  rangeLabel,
  discountCount,
}: {
  checked: boolean;
  icon: any;
  label: string;
  rangeLabel?: string;
  discountCount?: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex w-full items-center gap-4 py-1">
      <RadioDot checked={checked} />
      <div>
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>

      <div className="flex flex-1 min-w-0 items-center gap-3">
        <div className="font-semibold text-primary text-lg truncate">{label}</div>
        {rangeLabel ? (
          <div className="text-sm font-normal text-muted-foreground truncate">{rangeLabel}</div>
        ) : null}
      </div>

      {!!discountCount && (
        <span className="flex gap-3 items-center rounded-full bg-[#0A4DBD] text-white px-3 py-1 text-sm mr-4">
          <TicketPercent className="h-4 w-4" />
          {t("fe.pages.flow.steps.paymentMethod.discountsAvailable", {
            count: discountCount,
          })}
        </span>
      )}
    </div>
  );
}

function InstallmentCard({
  checked,
  title,
  subtitle,
  onClick,
  disabled,
}: {
  checked: boolean;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "flex min-h-16 gap-2 px-3 py-2 border-1 rounded-xl relative cursor-pointer transition-all",
        checked ? "border-primary bg-muted" : "",
        disabled ? "opacity-60 cursor-not-allowed" : ""
      )}
    >
      <div className="flex items-start gap-3">
        <RadioDot checked={checked} />
        <div className="min-w-0 flex flex-col items-start">
          <div className="text-base mb-1 font-semibold text-foreground leading-none">
            {title}
          </div>
          {subtitle ? (
            <div className="flex text-left gap-1 text-[#0A4DBD] text-xs">{subtitle}</div>
          ) : null}
        </div>
      </div>
    </button>
  );
}

function PaymentSchedule({
  contractId,
  paymentPlanId,
  hasActiveSubscription,
  subscriptionActive,
  promotion,
  promotionId,
  promotionStopAt,
  planStartDate,
  installments,
  paymentTiming,
  setPaymentTiming,
  payInFullAllowed,
  payInFull,
  setPayInFull,
}: {
  contractId: string;
  paymentPlanId: string;
  promotion?: PromotionResource;
  promotionId?: string | number;
  promotionStopAt?: string | null;
  planStartDate?: string | null;
  installments?: number;
  paymentTiming: PaymentTiming;
  setPaymentTiming: (timing: PaymentTiming) => void;
  payInFullAllowed: boolean;
  payInFull: boolean;
  setPayInFull: (payInFull: boolean) => void;
  hasActiveSubscription: boolean;
  subscriptionActive?: SubscriptionActiveResource;
}) {
  const { i18n, t } = useTranslation();

  const appliedPromotionId = promotionId ?? subscriptionActive?.attributes?.payment_snapshot?.appliedPromotion?.id;

  // The offer is the plan's to make and the customer's to take.
  const canPayInFull = payInFullAllowed && !hasActiveSubscription;
  const paysInFull = canPayInFull && payInFull;

  // Only a promotion whose deadline this plan would actually miss gets a say in the
  // payment date — the same test the payment step applies, so both quotes land on
  // the same react-query entry instead of fetching the identical schedule twice.
  // Settling today meets every deadline, so a plan being paid in full has none left
  // to miss: nothing to ask about the payment date, and nothing to forfeit.
  const planMissesDeadline = !paysInFull && planMissesPromotionDeadline(promotion, planStartDate);
  const effectivePaymentTiming = planMissesDeadline && !hasActiveSubscription ? paymentTiming : undefined;
  const forfeitsDeadlineDiscount = planMissesDeadline && paymentTiming === "plan_schedule";

  const { data: paymentSubscriptionData, isFetched, isFetching } = useOnlinePaymentOptions(
    contractId,
    paymentPlanId,
    appliedPromotionId,
    effectivePaymentTiming,
    paysInFull,
  );

  const pendingInitial = paymentSubscriptionData?.data?.meta?.initial_payment as TransactionType | undefined;
  const pendingSchedule = (paymentSubscriptionData?.data?.meta?.schedule ?? []) as TransactionType[];
  const activeSchedule = subscriptionActive?.relationships?.schedule ?? [];

  return (
    <div className="mt-8">
      <div className="text-base font-semibold text-foreground">
        {t("fe.pages.flow.steps.paymentMethod.listOfPaymentsTitle")}
      </div>
      <div className="mt-1 text-sm text-muted-foreground">
   
      </div>
      {/*
        * A customer who chose to pay on the plan's own date has given the discount
        * up, so this must not go on promising it — least of all directly above the
        * choice they just made.
        */}
      {!forfeitsDeadlineDiscount &&
        (promotionStopAt ?? subscriptionActive?.attributes?.payment_snapshot?.appliedPromotion?.stopAt) && (
        <div className="mt-1 text-sm font-medium text-[#0A4DBD]">
          {t("fe.pages.flow.steps.paymentMethod.promoValidUntil", {
            date: formatDate(
              (promotionStopAt ?? subscriptionActive?.attributes?.payment_snapshot?.appliedPromotion?.stopAt) as string,
              i18n.language,
            ),
            defaultValue: "Discount valid only if paid before {{date}}",
          })}
        </div>
      )}

      {canPayInFull && (
        <PayInFullChoice
          amount={paymentSubscriptionData?.meta?.balance}
          checked={payInFull}
          onChange={setPayInFull}
        />
      )}

      {!hasActiveSubscription && !paysInFull && (
        <PaymentTimingChoice
          promotion={promotion}
          planStartDate={planStartDate}
          installments={installments}
          value={paymentTiming}
          onChange={setPaymentTiming}
        />
      )}

      <div className="mt-4 rounded-xl border border-muted-foreground/20 overflow-hidden bg-white">
        {isFetching && <TableSkeleton rows={3} columns={3} />}
        {isFetched && !isFetching && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="px-6 text-muted-foreground font-medium text-sm">
                  {t("fe.pages.flow.steps.paymentMethod.table.headings.date")}
                </TableHead>
                <TableHead className="px-6 text-muted-foreground font-medium text-sm">
                  {t(
                    "fe.pages.flow.steps.paymentMethod.table.headings.amount"
                  )}
                </TableHead>
                <TableHead className="px-6 text-muted-foreground font-medium text-sm">
                  {t(
                    "fe.pages.flow.steps.paymentMethod.table.headings.status"
                  )}
                </TableHead>
              </TableRow>
            </TableHeader>
              <TableBody>
                {hasActiveSubscription ? (
                  activeSchedule.length > 0 ? (
                    activeSchedule.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {formatDate(item.attributes.date, i18n.language)}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {formatCurrency(Number(item.attributes.amount), i18n.language)}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {item.relationships.invoice ? (
                            <StatusChip label={t("fe.pages.flow.steps.payment.statusPayed")} variant="paid" />
                          ) : (
                            <StatusChip label={t("fe.pages.flow.steps.payment.statusScheduled")} variant="scheduled" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <EmptyScheduleRow t={t} />
                  )
                ) : !pendingInitial && pendingSchedule.length === 0 ? (
                  <EmptyScheduleRow t={t} reason={emptyScheduleReason(paymentSubscriptionData?.meta)} />
                ) : (
                  <>
                    {pendingInitial && (
                      <TableRow>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {formatDate(pendingInitial.date, i18n.language)}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {formatCurrency(pendingInitial.amount, i18n.language)}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          <StatusChip label={t("fe.pages.flow.steps.payment.statusWaiting")} variant="pending" />
                        </TableCell>
                      </TableRow>
                    )}
                    {pendingSchedule.map((transaction: TransactionType, index: number) => (
                      <TableRow key={index}>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {formatDate(transaction.date, i18n.language)}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          {formatCurrency(transaction.amount, i18n.language)}
                        </TableCell>
                        <TableCell className="px-6 font-normal h-12 text-sm text-foreground">
                          <StatusChip label={t("fe.pages.flow.steps.payment.statusScheduled")} variant="scheduled" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function EmptyScheduleRow({ t, reason }: { t: (key: string) => string; reason?: string | null }) {
  return (
    <TableRow>
      <TableCell
        colSpan={3}
        className="px-6 h-20 text-center text-sm text-muted-foreground"
      >
        {reason ?? t("fe.pages.flow.steps.paymentMethod.table.emptyState")}
      </TableCell>
    </TableRow>
  );
}